import { Box } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { INTEGRATION_SUMMARY } from "../../../../../theme/tokens";
import { type IntegrationShiftsSummary, type ShiftMismatchEmployeeRow, WageType } from "../../../../../utils/models";
import { displayNullable, EMPTY_VALUE, gridSx } from "./consts";
import { EmptySummaryState } from "./EmptySummaryState";
import { SectionHeading, sectionHeadingCountProps } from "./SectionHeading";

const PAY_TYPE_KEYS: Record<WageType, string> = {
	[WageType.Hourly]: "integration.summary.shifts.payType.hourly",
	[WageType.Annually]: "integration.summary.shifts.payType.annually",
};

const TEST_ID = "hris-shifts-summary-employees";

export const ShiftMismatchEmployeesGrid: FC<{ section: IntegrationShiftsSummary["employees"] }> = ({ section }) => {
	const { t } = useTranslation();
	const columns = useMemo<GridColDef<ShiftMismatchEmployeeRow>[]>(() => {
		const yesNo = (value: boolean) => t(value ? "integration.summary.status.yes" : "integration.summary.status.no");
		return [
			{ field: "fullName", headerName: t("integration.summary.column.employee"), flex: 1, minWidth: 150 },
			{
				field: "jobName",
				headerName: t("integration.summary.column.job"),
				flex: 0.8,
				minWidth: 110,
				valueGetter: (_value, row) => displayNullable(row.jobName),
			},
			{
				field: "externalRole",
				headerName: t("integration.summary.shifts.column.externalRole"),
				flex: 0.9,
				minWidth: 120,
				valueGetter: (_value, row) => displayNullable(row.externalRole),
			},
			{
				field: "payType",
				headerName: t("integration.summary.shifts.column.payType"),
				width: 100,
				valueGetter: (_value, row) => (row.payType ? t(PAY_TYPE_KEYS[row.payType]) : EMPTY_VALUE),
			},
			{ field: "actualWithoutScheduleCount", headerName: t("integration.summary.shifts.column.noSchedule"), width: 140 },
			{ field: "scheduledWithoutActualCount", headerName: t("integration.summary.shifts.column.noPunches"), width: 150 },
			{ field: "actualCount", headerName: t("integration.summary.column.actualShifts"), width: 110 },
			{ field: "scheduledCount", headerName: t("integration.summary.column.scheduledShifts"), width: 120 },
			{
				field: "hasAnySchedule",
				headerName: t("integration.summary.shifts.column.hasAnySchedule"),
				width: 140,
				valueGetter: (_value, row) => yesNo(row.hasAnySchedule),
			},
			{
				field: "hasAnyActual",
				headerName: t("integration.summary.shifts.column.hasAnyActual"),
				width: 130,
				valueGetter: (_value, row) => yesNo(row.hasAnyActual),
			},
		];
	}, [t]);
	return (
		<>
			<SectionHeading
				title={t("integration.summary.shifts.employees.title")}
				description={t("integration.summary.shifts.employees.description")}
				{...sectionHeadingCountProps(section)}
			/>
			<Box sx={gridSx} data-testid={`${TEST_ID}-container`}>
				<SystemDataGrid<ShiftMismatchEmployeeRow>
					rows={section.rows}
					columns={columns}
					getRowId={(row) => row.nurseId}
					searchableColumns={["fullName"]}
					dataTestId={TEST_ID}
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					emptyState={
						<EmptySummaryState
							title={t("integration.summary.shifts.noMismatches.title")}
							hint={t("integration.summary.shifts.noMismatches.hint")}
							testId={`${TEST_ID}-empty`}
						/>
					}
				/>
			</Box>
		</>
	);
};
